/**
 * Token storage. Isolated so there is exactly one place that knows the key
 * name and one place to change if we ever move off localStorage.
 *
 * Sep-3: added lastActivity + sessionStart timestamps for wall-clock idle
 * enforcement. Rationale — an in-page setTimeout pauses when the laptop
 * sleeps, so 10 hours of sleep counted as ~0 idle. A persistent timestamp
 * survives sleep AND full browser restarts, so the auth restore path can
 * catch stale sessions before letting the app render.
 */
const TOKEN_KEY          = "specta.token";
const LAST_ACTIVITY_KEY  = "specta.session.lastActivityAt";
const SESSION_START_KEY  = "specta.session.startedAt";

export function getToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setToken(token: string): void {
  try {
    localStorage.setItem(TOKEN_KEY, token);
  } catch {
    /* private mode / storage disabled — the session just won't persist */
  }
}

export function clearToken(): void {
  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* ignore */
  }
}

/* ---------- wall-clock session timestamps (Sep-3) ----------
 * Both are millisecond epoch ints stored as strings.
 * NULL means "no record" — treat as "session state unknown / do the
 * safe thing" (usually: session hasn't started yet, or was cleared).
 */

/** Reads the last recorded activity time. */
export function getLastActivity(): number | null {
  try {
    const raw = localStorage.getItem(LAST_ACTIVITY_KEY);
    if (raw === null) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

/** Updates the last activity time. Called on each throttled activity
 *  tick from useIdleTimeout so a busy user keeps their session fresh
 *  even across a browser refresh or laptop wake. */
export function setLastActivity(ts: number = Date.now()): void {
  try {
    localStorage.setItem(LAST_ACTIVITY_KEY, String(ts));
  } catch {
    /* ignore */
  }
}

export function clearLastActivity(): void {
  try {
    localStorage.removeItem(LAST_ACTIVITY_KEY);
  } catch {
    /* ignore */
  }
}

/** Reads when this session began. */
export function getSessionStart(): number | null {
  try {
    const raw = localStorage.getItem(SESSION_START_KEY);
    if (raw === null) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

/** Sets the session-start timestamp — call on successful login only, so
 *  the absolute max-age clock starts fresh with each new session. */
export function setSessionStart(ts: number = Date.now()): void {
  try {
    localStorage.setItem(SESSION_START_KEY, String(ts));
  } catch {
    /* ignore */
  }
}

export function clearSessionStart(): void {
  try {
    localStorage.removeItem(SESSION_START_KEY);
  } catch {
    /* ignore */
  }
}
